// Import necessary dependencies from Redux Toolkit and Firebase
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { LoadFormData } from '../../config/types';
import { db } from '../../config/firebase';
import { collection, addDoc, getDocs, query, orderBy, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../config/firebase';

// Define the shape of our dispatch state
interface LoadWithId extends LoadFormData {
    id: string;
    fileUrl?: string;
}

interface DispatchState {
    loads: LoadWithId[];      // Array to store all load records
    loading: boolean;           // Loading state for async operations
    error: string | null;       // Error message if any operation fails
}

// Initial state for the dispatch slice
const initialState: DispatchState = {
    loads: [],                  // Start with empty loads array
    loading: false,             // Initially not loading
    error: null,                // No initial errors
}

// Helper function to get the current month folder name
const getCurrentMonthFolder = () => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

// Async thunk for uploading a load to Firestore
// This thunk handles the creation of new load records in the database
export const uploadLoad = createAsyncThunk(
    'dispatch/uploadLoad',
    async (payload: { loadData: LoadFormData; file?: File }) => {
        try {
            let fileUrl: string | undefined = undefined;
            
            // Handle file upload if a file is provided
            if (payload.file) {
                try {
                    // Create the storage path: Load/{current Month}/DispatchRequests/{filename}
                    const monthFolder = getCurrentMonthFolder();
                    const filePath = `Load/${monthFolder}/DispatchRequests/${payload.file.name}`;
                    const storageRef = ref(storage, filePath);
                    
                    // Upload the file
                    await uploadBytes(storageRef, payload.file);
                    
                    // Get the download URL
                    fileUrl = await getDownloadURL(storageRef);
                } catch (error: any) {
                    console.error('File upload error:', error);
                    throw new Error(`File upload failed: ${error.message || 'Unknown error'}`);
                }
            }
            
            try {
                // Create a reference to the loads collection
                const loadsRef = collection(db, 'loads');
                
                // Prepare the data for upload
                const loadData: Record<string, any> = {
                    ...payload.loadData,
                    createdAt: new Date().toISOString(),
                    fileUrl
                };

                // Remove any undefined or null values
                Object.keys(loadData).forEach(key => {
                    if (loadData[key] === undefined || loadData[key] === null) {
                        delete loadData[key];
                    }
                });
                
                // Add the document to Firestore
                const docRef = await addDoc(loadsRef, loadData);
                
                // Return the load data with the new document ID and file URL
                return { 
                    ...payload.loadData, 
                    id: docRef.id,
                    fileUrl 
                } as LoadWithId;
            } catch (error: any) {
                console.error('Firestore upload error:', error);
                throw new Error(`Database upload failed: ${error.message || 'Unknown error'}`);
            }
        } catch (error: any) {
            console.error('Upload load error:', error);
            throw error;
        }
    }
);

// Async thunk for fetching all loads from Firestore
// This thunk retrieves all load records from the database
export const fetchLoads = createAsyncThunk(
    'dispatch/fetchLoads',
    async () => {
        try {
            // Create a reference to the loads collection
            const loadsRef = collection(db, 'loads');
            // Create a query to sort loads by creation date
            const q = query(loadsRef, orderBy('createdAt', 'desc'));
            // Execute the query
            const querySnapshot = await getDocs(q);
            // Map the documents to our LoadFormData type
            const loads = querySnapshot.docs.map(doc => ({
                ...(doc.data() as LoadFormData),
                id: doc.id
            }));
            return loads;
        } catch (error) {
            throw new Error('Failed to fetch loads');
        }
    }
);

// Async thunk for deleting a load from Firestore and its associated file from Storage
export const deleteLoad = createAsyncThunk(
    'dispatch/deleteLoad',
    async (payload: { loadId: string; fileUrl?: string }) => {
        try {
            // Delete the file from Storage if it exists
            if (payload.fileUrl) {
                try {
                    const fileRef = ref(storage, payload.fileUrl);
                    await deleteObject(fileRef);
                } catch (error) {
                    console.error('Error deleting file:', error);
                    // Continue with document deletion even if file deletion fails
                }
            }

            // Delete the document from Firestore
            const loadRef = doc(db, 'loads', payload.loadId);
            await deleteDoc(loadRef);

            return payload.loadId;
        } catch (error) {
            throw new Error('Failed to delete load');
        }
    }
);

// Async thunk for updating a load's status
export const updateLoadStatus = createAsyncThunk(
    'dispatch/updateLoadStatus',
    async (payload: { loadId: string; status: LoadFormData['status'] }) => {
        try {
            const loadRef = doc(db, 'loads', payload.loadId);
            await updateDoc(loadRef, {
                status: payload.status,
                updatedAt: new Date().toISOString()
            });

            return { id: payload.loadId, status: payload.status };
        } catch (error) {
            throw new Error('Failed to update load status');
        }
    }
);

// Async thunk for updating a load
export const updateLoad = createAsyncThunk(
    'dispatch/updateLoad',
    async (payload: { 
        loadId: string; 
        loadData: LoadFormData; 
        currentFileUrl?: string;
        file?: File 
    }) => {
        try {
            let fileUrl = payload.currentFileUrl;
            
            // Handle file changes
            if (payload.file) {
                try {
                    // If there's a new file and it's different from the current one
                    if (!fileUrl || (fileUrl && payload.file.name !== payload.loadData.pdfFileName)) {
                        // Delete the old file if it exists
                        if (fileUrl) {
                            try {
                                const oldFileRef = ref(storage, fileUrl);
                                await deleteObject(oldFileRef);
                            } catch (error) {
                                console.error('Error deleting old file:', error);
                                // Continue with update even if old file deletion fails
                            }
                        }

                        // Upload the new file
                        const monthFolder = getCurrentMonthFolder();
                        const filePath = `Load/${monthFolder}/DispatchRequests/${payload.file.name}`;
                        const storageRef = ref(storage, filePath);
                        
                        await uploadBytes(storageRef, payload.file);
                        fileUrl = await getDownloadURL(storageRef);
                    }
                } catch (error: any) {
                    console.error('File handling error:', error);
                    throw new Error(`File handling failed: ${error.message || 'Unknown error'}`);
                }
            } else if (fileUrl && !payload.loadData.pdfFile) {
                // If there was a file but it's been removed
                try {
                    const oldFileRef = ref(storage, fileUrl);
                    await deleteObject(oldFileRef);
                    fileUrl = undefined;
                } catch (error) {
                    console.error('Error deleting old file:', error);
                }
            }

            // Prepare update data by removing undefined/null values
            const updateData: Record<string, any> = {
                ...payload.loadData,
                fileUrl,
                updatedAt: new Date().toISOString()
            };

            // Remove any undefined or null values to prevent Firestore errors
            Object.keys(updateData).forEach(key => {
                if (updateData[key] === undefined || updateData[key] === null) {
                    delete updateData[key];
                }
            });

            try {
                // Update the document in Firestore
                const loadRef = doc(db, 'loads', payload.loadId);
                await updateDoc(loadRef, updateData);

                // Return the updated load data
                return { 
                    ...payload.loadData,
                    id: payload.loadId,
                    fileUrl
                } as LoadWithId;
            } catch (error: any) {
                console.error('Firestore update error:', error);
                throw new Error(`Database update failed: ${error.message || 'Unknown error'}`);
            }
        } catch (error: any) {
            console.error('Update load error:', error);
            throw error;
        }
    }
);

// Create the dispatch slice with reducers and extra reducers for async actions
const dispatchSlice = createSlice({
    name: 'dispatch',
    initialState,
    // No standard reducers as we're only using async actions
    reducers: {},
    // Extra reducers to handle the async action lifecycle
    extraReducers: (builder) => {
        // Upload load cases
        builder
            // When upload starts
            .addCase(uploadLoad.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            // When upload succeeds
            .addCase(uploadLoad.fulfilled, (state, action) => {
                state.loading = false;
                state.loads.unshift(action.payload);  // Add new load to the beginning of the array
                state.error = null;
            })
            // When upload fails
            .addCase(uploadLoad.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to upload load';
            })
            // Fetch loads cases
            // When fetch starts
            .addCase(fetchLoads.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            // When fetch succeeds
            .addCase(fetchLoads.fulfilled, (state, action) => {
                state.loading = false;
                state.loads = action.payload;  // Replace existing loads with fetched loads
                state.error = null;
            })
            // When fetch fails
            .addCase(fetchLoads.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch loads';
            })
            // Delete load cases
            .addCase(deleteLoad.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteLoad.fulfilled, (state, action) => {
                state.loading = false;
                state.loads = state.loads.filter(load => load.id !== action.payload);
                state.error = null;
            })
            .addCase(deleteLoad.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to delete load';
            })
            // Update load status cases
            .addCase(updateLoadStatus.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateLoadStatus.fulfilled, (state, action) => {
                state.loading = false;
                state.loads = state.loads.map(load => 
                    load.id === action.payload.id 
                        ? { ...load, status: action.payload.status }
                        : load
                );
                state.error = null;
            })
            .addCase(updateLoadStatus.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to update load status';
            })
            // Update load cases
            .addCase(updateLoad.pending, (state) => {
            state.loading = true;
                state.error = null;
            })
            .addCase(updateLoad.fulfilled, (state, action) => {
                state.loading = false;
                state.loads = state.loads.map(load =>
                    load.id === action.payload.id ? action.payload : load
                );
                state.error = null;
            })
            .addCase(updateLoad.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to update load';
        });
    },
});

// Export the empty action creators object and the reducer
export const { } = dispatchSlice.actions;
export default dispatchSlice.reducer;




